import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

const WordGrid = ({ grid, words, foundWords, onWordFound }) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [startCell, setStartCell] = useState(null);
  const [foundWordCells, setFoundWordCells] = useState(new Set());
  const gridRef = useRef(null);

  const getCellKey = (row, col) => `${row}-${col}`;

  const handleMouseDown = (row, col) => {
    setIsSelecting(true);
    setStartCell({ row, col });
    setSelectedCells([getCellKey(row, col)]);
  };

  const handleMouseEnter = (row, col) => {
    if (!isSelecting || !startCell) return;

    const newSelectedCells = getLineCells(startCell.row, startCell.col, row, col);
    setSelectedCells(newSelectedCells);
  };

  const handleMouseUp = () => {
    if (selectedCells.length > 0) {
      checkForWord();
    }
    setIsSelecting(false);
    setSelectedCells([]);
    setStartCell(null);
  };

  const getLineCells = (startRow, startCol, endRow, endCol) => {
    const cells = [];
    const deltaRow = endRow - startRow;
    const deltaCol = endCol - startCol;
    
    // Check if it's a valid line (horizontal, vertical, or diagonal)
    const isHorizontal = deltaRow === 0;
    const isVertical = deltaCol === 0;
    const isDiagonal = Math.abs(deltaRow) === Math.abs(deltaCol);
    
    if (!isHorizontal && !isVertical && !isDiagonal) {
      return [getCellKey(startRow, startCol)];
    }

    const steps = Math.max(Math.abs(deltaRow), Math.abs(deltaCol));
    const stepRow = steps === 0 ? 0 : deltaRow / steps;
    const stepCol = steps === 0 ? 0 : deltaCol / steps;

    for (let i = 0; i <= steps; i++) {
      const row = startRow + Math.round(stepRow * i);
      const col = startCol + Math.round(stepCol * i);
      cells.push(getCellKey(row, col));
    }

    return cells;
  };

  const checkForWord = () => {
    const selectedWord = selectedCells.map(cellKey => {
      const [row, col] = cellKey.split('-').map(Number);
      return grid[row][col];
    }).join('');

    const reverseWord = selectedWord.split('').reverse().join('');
    
    const foundWord = words.find(word => 
      (word === selectedWord || word === reverseWord) && 
      !foundWords.includes(word)
    );

    if (foundWord) {
      const coordinates = selectedCells.map(cellKey => {
        const [row, col] = cellKey.split('-').map(Number);
        return { row, col };
      });
      
      // Mark cells as found
      const newFoundCells = new Set(foundWordCells);
      selectedCells.forEach(cellKey => newFoundCells.add(cellKey));
      setFoundWordCells(newFoundCells);
      
      onWordFound(foundWord, coordinates);
    }
  };

  const isCellFound = (row, col) => {
    return foundWordCells.has(getCellKey(row, col));
  };

  // Touch support for mobile
  const handleTouchStart = (e, row, col) => {
    e.preventDefault();
    handleMouseDown(row, col);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isSelecting) return;

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row && element.dataset.col) {
      const row = parseInt(element.dataset.row);
      const col = parseInt(element.dataset.col);
      handleMouseEnter(row, col);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleMouseUp();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
      <h2 className="text-xl font-semibold text-[#2b6777] mb-4 font-rubik">Buchstabengitter</h2>
      
      <div 
        ref={gridRef}
        className="inline-block select-none"
        onMouseLeave={() => {
          if (isSelecting) {
            handleMouseUp();
          }
        }}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="grid gap-1" 
          style={{ gridTemplateColumns: `repeat(${grid[0].length}, 1fr)` }}
        >
          {grid.map((row, rowIndex) =>
            row.map((letter, colIndex) => {
              const cellKey = getCellKey(rowIndex, colIndex);
              const isSelected = selectedCells.includes(cellKey);
              const isFound = isCellFound(rowIndex, colIndex);
              
              return (
                <motion.div
                  key={cellKey}
                  data-row={rowIndex}
                  data-col={colIndex}
                  className={`
                    w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 
                    flex items-center justify-center 
                    border-2 border-gray-300 
                    font-bold text-sm md:text-base lg:text-lg
                    cursor-pointer transition-all duration-200
                    text-[#2b6777] font-rubik
                    ${isSelected ? 'bg-blue-200 border-blue-400' : ''}
                    ${isFound ? 'bg-green-200 border-green-400' : ''}
                    ${!isSelected && !isFound ? 'hover:bg-gray-100' : ''}
                  `}
                  onMouseDown={() => handleMouseDown(rowIndex, colIndex)}
                  onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
                  onMouseUp={handleMouseUp}
                  onTouchStart={(e) => handleTouchStart(e, rowIndex, colIndex)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {letter}
                </motion.div>
              );
            })
          )}
        </div>
      </div>
      
      <div className="mt-4 text-sm text-[#2b6777] font-rubik opacity-80">
        <p>Ziehe mit der Maus über die Buchstaben, um Wörter zu markieren.</p>
        <p>Wörter können horizontal, vertikal oder diagonal verlaufen.</p>
      </div>
    </div>
  );
};

export default WordGrid;